import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import {
  BackgroundColors,
  AbsoluteWhiteColors,
} from '@picsart/design-system/foundation/colors';
import { Effects } from '@picsart/design-system/foundation/effects';
import {
  FontSizes,
  LineHeights,
} from '@picsart/design-system/foundation/typography';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';
import { canHover } from '@picsart/design-system/styleUtils/hoverTouch';

const useStyles = createUseStyles(
  {
    '@keyframes fadeOutScale': {
      '0%': {
        opacity: 1,
        transform: 'scale(1)',
      },
      '100%': {
        opacity: 0,
        transform: 'scale(0.95)',
      },
    },
    preview: {
      position: 'relative',
      width: '100%',
      height: 144,
      borderRadius: CornerRadius.r12,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: BackgroundColors.tint1, // Match dropzone background
    },
    previewRemoving: {
      animation: `$fadeOutScale 0.15s ${Easings.easeOut} forwards`,
    },
    // Compact preview - narrower width, same height
    previewCompact: {
      flex: 'none', // Don't stretch to fill
      width: 120, // Fixed narrow width
      height: '100%', // Fill parent height
      [`@media (max-width: ${Breakpoints.xs}px)`]: {
        width: 100,
      },
    },
    previewMedia: {
      display: 'block',
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      objectFit: 'cover', // Fill the box, cropping if needed
      borderRadius: CornerRadius.r12,
    },
    removeButton: {
      position: 'absolute',
      top: Spacings.s8,
      right: Spacings.s8,
      display: 'inline-flex',
      zIndex: 2,
    },
    iconButton: {
      width: Spacings.s32,
      height: Spacings.s32,
      flex: `0 0 ${Spacings.s32}`,
      borderRadius: CornerRadius.circle,
      backgroundColor: Effects.alpha50,
      color: AbsoluteWhiteColors.base.default,
      border: 'none',
      [canHover]: {
        '&:hover': {
          backgroundColor: Effects.alpha80,
        },
      },
    },
    // Dimmed, NOT blurred. A `backdrop-filter` was tried here and reverted: at
    // any radius strong enough to frost, the blur is a large fraction of a
    // 120px tile, so it flattens the photo into a colour field and the scrim
    // turns that to mud — losing the preview, which is the whole point of this
    // state (it is what tells the user their file arrived). The scrim alone
    // keeps the photo readable while still saying "not ready yet". Fades in so
    // a fast upload doesn't flash a hard rectangle.
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: Spacings.s8,
      padding: Spacings.s8,
      boxSizing: 'border-box',
      backgroundColor: Effects.alpha50,
      animation: `$fadeIn 0.2s ${Easings.easeOut}`,
    },
    // The default spinner's track is a theme background token, which all but
    // disappears against a dark scrim over media — the tile then looked frozen,
    // which is the exact impression this overlay exists to prevent. Same
    // translucent-track/white-arc pairing `FeedActionBar` uses over media.
    loadingSpinner: {
      borderColor: Effects.alpha20,
      borderTopColor: AbsoluteWhiteColors.base.default,
    },
    // Wraps rather than truncates: the tile is 120px wide at its narrowest, so
    // even the short copy needs two lines there. Semibold because it sits over
    // media at 12px, where medium reads as washed out.
    loadingMessage: {
      fontSize: FontSizes.t3,
      lineHeight: LineHeights.t3,
      fontWeight: FontWeights.semibold,
      color: AbsoluteWhiteColors.base.default,
      textAlign: 'center',
    },
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    '@media (prefers-reduced-motion: reduce)': {
      loadingOverlay: { animation: 'none' },
    },
  },
  { name: 'file-preview' }
);

export default useStyles;
